import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  Token as TokenIcon,
  CalendarToday as CalendarIcon,
  Analytics as AnalyticsIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import api from '../api/api';
import {
  SubscriptionPlan,
  ActivationToken,
  AcademicTerm,
  SubscriptionStats,
  getSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
  getActivationTokens,
  createActivationToken,
  getAcademicTerms,
  createAcademicTerm,
  updateAcademicTerm,
  deleteAcademicTerm,
  getSubscriptionStats
} from '../services/subscriptionService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`subscription-tabpanel-${index}`}
      aria-labelledby={`subscription-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SubscriptionManagement: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  // Plans state
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [planForm, setPlanForm] = useState<{
    name: string;
    description: string;
    price_per_term: number;
    price_per_year: number;
    max_students: number;
    max_cameras: number;
    is_active: boolean;
    features: {
      facial_recognition: boolean;
      attendance_reports: boolean;
      mobile_app: boolean;
      api_access: boolean;
      priority_support: boolean;
    };
  }>({
    name: '',
    description: '',
    price_per_term: 0,
    price_per_year: 0,
    max_students: 0,
    max_cameras: 0,
    is_active: true,
    features: {
      facial_recognition: true,
      attendance_reports: true,
      mobile_app: true,
      api_access: false,
      priority_support: false
    }
  });

  // Tokens state
  const [tokens, setTokens] = useState<ActivationToken[]>([]);
  const [tokenDialog, setTokenDialog] = useState(false);
  const [tokenForm, setTokenForm] = useState({
    plan_id: '',
    subscription_type: 'term' as 'term' | 'year',
    expires_at: ''
  });

  // Terms state
  const [terms, setTerms] = useState<AcademicTerm[]>([]);
  const [termDialog, setTermDialog] = useState(false);
  const [editingTerm, setEditingTerm] = useState<AcademicTerm | null>(null);
  const [termForm, setTermForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    term_number: 1,
    start_date: '',
    end_date: '',
    is_active: false
  });

  // Stats state
  const [stats, setStats] = useState<SubscriptionStats | null>(null);

  // Active subscriptions state
  const [activeSubscriptions, setActiveSubscriptions] = useState<any[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'superuser') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [plansData, tokensData, termsData, statsData] = await Promise.all([
        getSubscriptionPlans(false), // Get all plans, not just active ones
        getActivationTokens(),
        getAcademicTerms(),
        getSubscriptionStats()
      ]);

      setPlans(plansData as any[]);
      setTokens(tokensData as any[]);
      setTerms(termsData as any[]);
      setStats(statsData as any);
    } catch (err: any) {
      console.error('Failed to fetch subscription data:', err);
      if (err?.response?.data?.detail) {
        const detail = err.response.data.detail;
        setError(Array.isArray(detail) 
          ? detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
          : typeof detail === 'string' ? detail : String(detail));
      } else {
        setError('Failed to load subscription data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSubscriptions = async () => {
    setSubscriptionsLoading(true);
    try {
      const response = await api.get('/subscriptions?status=active');
      setActiveSubscriptions(response.data as any[]);
    } catch (err: any) {
      console.error('Failed to fetch active subscriptions:', err);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const handleGenerateInvoice = async (subscriptionId: number) => {
    try {
      await api.post(`/invoices/subscription/${subscriptionId}/create-invoice`);
      // Show success message or refresh data
      alert('Invoice generated successfully!');
    } catch (err: any) {
      console.error('Failed to generate invoice:', err);
      alert(err?.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Plan management functions
  const handlePlanSubmit = async () => {
    try {
      if (editingPlan) {
        await updateSubscriptionPlan(editingPlan.id, planForm);
      } else {
        await createSubscriptionPlan(planForm);
      }
      setPlanDialog(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchData();
    } catch (err: any) {
      console.error('Plan save error:', err);
      if (err?.response?.data?.detail) {
        const detail = err.response.data.detail;
        setError(Array.isArray(detail) 
          ? detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
          : typeof detail === 'string' ? detail : String(detail));
      } else {
        setError('Failed to save plan');
      }
    }
  };

  const handlePlanEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      description: plan.description,
      price_per_term: plan.price_per_term,
      price_per_year: plan.price_per_year,
      max_students: plan.max_students,
      max_cameras: plan.max_cameras,
      is_active: plan.is_active,
      features: {
        facial_recognition: plan.features.facial_recognition ?? true,
        attendance_reports: plan.features.attendance_reports ?? true,
        mobile_app: plan.features.mobile_app ?? true,
        api_access: plan.features.api_access ?? false,
        priority_support: plan.features.priority_support ?? false
      }
    });
    setPlanDialog(true);
  };

  const handlePlanDelete = async (planId: number) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await deleteSubscriptionPlan(planId);
        fetchData();
      } catch (err: any) {
        console.error('Plan delete error:', err);
        if (err?.response?.data?.detail) {
          const detail = err.response.data.detail;
          setError(Array.isArray(detail) 
            ? detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
            : typeof detail === 'string' ? detail : String(detail));
        } else {
          setError('Failed to delete plan');
        }
      }
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      description: '',
      price_per_term: 0,
      price_per_year: 0,
      max_students: 0,
      max_cameras: 0,
      is_active: true,
      features: {
        facial_recognition: true,
        attendance_reports: true,
        mobile_app: true,
        api_access: false,
        priority_support: false
      }
    });
  };

  // Token management functions
  const handleTokenSubmit = async () => {
    try {
      const tokenData: any = {
        plan_id: parseInt(tokenForm.plan_id),
        subscription_type: tokenForm.subscription_type
      };
      
      // Only include expires_at if it's provided and not empty
      if (tokenForm.expires_at && tokenForm.expires_at.trim() !== '') {
        // Convert the date string to ISO format
        const date = new Date(tokenForm.expires_at);
        tokenData.expires_at = date.toISOString();
      }
      
      await createActivationToken(tokenData);
      setTokenDialog(false);
      setTokenForm({ plan_id: '', subscription_type: 'term', expires_at: '' });
      fetchData();
    } catch (err: any) {
      console.error('Token creation error:', err);
      if (err?.response?.data?.detail) {
        const detail = err.response.data.detail;
        setError(Array.isArray(detail) 
          ? detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
          : typeof detail === 'string' ? detail : String(detail));
      } else {
        setError('Failed to create token');
      }
    }
  };

  const copyToken = async (token: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(token);
        setError(''); // Clear any existing errors
        setCopySuccess('Token copied to clipboard successfully!');
        setTimeout(() => setCopySuccess(null), 3000);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = token;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setError(''); // Clear any existing errors
          setCopySuccess('Token copied to clipboard successfully!');
          setTimeout(() => setCopySuccess(null), 3000);
        } catch (fallbackErr) {
          console.error('Fallback copy failed:', fallbackErr);
          setError('Failed to copy token. Please copy manually: ' + token);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Failed to copy token:', err);
      setError('Failed to copy token to clipboard. Please copy manually: ' + token);
    }
  };

  // Term management functions
  const handleTermSubmit = async () => {
    try {
      if (editingTerm) {
        await updateAcademicTerm(editingTerm.id, termForm);
      } else {
        await createAcademicTerm(termForm);
      }
      setTermDialog(false);
      setEditingTerm(null);
      resetTermForm();
      fetchData();
    } catch (err: any) {
      console.error('Term save error:', err);
      if (err?.response?.data?.detail) {
        const detail = err.response.data.detail;
        setError(Array.isArray(detail) 
          ? detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
          : typeof detail === 'string' ? detail : String(detail));
      } else {
        setError('Failed to save term');
      }
    }
  };

  const handleTermEdit = (term: AcademicTerm) => {
    setEditingTerm(term);
    setTermForm({
      name: term.name,
      year: term.year,
      term_number: term.term_number,
      start_date: term.start_date.split('T')[0],
      end_date: term.end_date.split('T')[0],
      is_active: term.is_active
    });
    setTermDialog(true);
  };

  const handleTermDelete = async (termId: number) => {
    if (window.confirm('Are you sure you want to delete this term?')) {
      try {
        await deleteAcademicTerm(termId);
        fetchData();
      } catch (err: any) {
        console.error('Term delete error:', err);
        if (err?.response?.data?.detail) {
          const detail = err.response.data.detail;
          setError(Array.isArray(detail) 
            ? detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
            : typeof detail === 'string' ? detail : String(detail));
        } else {
          setError('Failed to delete term');
        }
      }
    }
  };

  const resetTermForm = () => {
    setTermForm({
      name: '',
      year: new Date().getFullYear(),
      term_number: 1,
      start_date: '',
      end_date: '',
      is_active: false
    });
  };

  if (user?.role !== 'superuser') {
    return <Alert severity="error">You are not authorized to view this page.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Subscription Management"
        subtitle="Manage subscription plans, activation tokens, and academic terms"
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {copySuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCopySuccess(null)}>
          {copySuccess}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="subscription tabs">
          <Tab icon={<AnalyticsIcon />} label="Statistics" />
          <Tab icon={<SchoolIcon />} label="Plans" />
          <Tab icon={<TokenIcon />} label="Tokens" />
          <Tab icon={<CalendarIcon />} label="Terms" />
          <Tab icon={<ReceiptIcon />} label="Active Subscriptions" />
        </Tabs>

        {/* Statistics Tab */}
        <TabPanel value={tabValue} index={0}>
          {stats && (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 3,
              '& > *': {
                flex: '1 1 200px',
                minWidth: '200px'
              }
            }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Schools
                  </Typography>
                  <Typography variant="h4">{stats.total_schools}</Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Subscriptions
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.active_subscriptions}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Expired Subscriptions
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.expired_subscriptions}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Revenue (KES)
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {stats.total_revenue_kes.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
          {stats && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Rate
                </Typography>
                <Typography variant="h3" color="primary.main">
                  {stats.subscription_rate.toFixed(1)}%
                </Typography>
                <Typography color="textSecondary">
                  {stats.active_subscriptions} out of {stats.total_schools} schools have active subscriptions
                </Typography>
              </CardContent>
            </Card>
          )}
        </TabPanel>

        {/* Plans Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingPlan(null);
                resetPlanForm();
                setPlanDialog(true);
              }}
            >
              Add Plan
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price (Term/Year)</TableCell>
                  <TableCell>Limits</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <Typography variant="subtitle1">{plan.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {plan.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        KES {plan.price_per_term.toLocaleString()} / {plan.price_per_year.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {plan.max_students} students, {plan.max_cameras} cameras
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.is_active ? 'Active' : 'Inactive'}
                        color={plan.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handlePlanEdit(plan)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handlePlanDelete(plan.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tokens Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setTokenDialog(true)}
            >
              Generate Token
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Token</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {token.token.substring(0, 8)}...
                        </Typography>
                        <Tooltip title="Copy token">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToken(token.token)}
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'success.light',
                                color: 'success.contrastText'
                              }
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{token.plan?.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={token.subscription_type}
                        size="small"
                        color={token.subscription_type === 'year' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={token.is_used ? 'Used' : 'Available'}
                        color={token.is_used ? 'default' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(token.expires_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {token.used_by_school && (
                        <Typography variant="body2" color="textSecondary">
                          Used by: {token.used_by_school.name}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Terms Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingTerm(null);
                resetTermForm();
                setTermDialog(true);
              }}
            >
              Add Term
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Term</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {terms.map((term) => (
                  <TableRow key={term.id}>
                    <TableCell>{term.name}</TableCell>
                    <TableCell>{term.year}</TableCell>
                    <TableCell>Term {term.term_number}</TableCell>
                    <TableCell>
                      {new Date(term.start_date).toLocaleDateString()} - {new Date(term.end_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={term.is_active ? 'Active' : 'Inactive'}
                        color={term.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleTermEdit(term)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleTermDelete(term.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Active Subscriptions Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchActiveSubscriptions}
            >
              Load Active Subscriptions
            </Button>
          </Box>

          {subscriptionsLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>School</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeSubscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell>{subscription.school?.name || subscription.school_id}</TableCell>
                      <TableCell>{subscription.plan?.name || 'Unknown Plan'}</TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.status}
                          color={subscription.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(subscription.start_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(subscription.end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<ReceiptIcon />}
                          onClick={() => handleGenerateInvoice(subscription.id)}
                        >
                          Generate Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Plan Dialog */}
      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Add Plan'}</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2,
            mt: 1
          }}>
            <TextField
              fullWidth
              label="Plan Name"
              value={planForm.name}
              onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <TextField
              fullWidth
              label="Price per Term (KES)"
              type="number"
              value={planForm.price_per_term}
              onChange={(e) => setPlanForm({ ...planForm, price_per_term: parseInt(e.target.value) })}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={planForm.description}
              onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
              sx={{ flex: '1 1 100%' }}
            />
            <TextField
              fullWidth
              label="Price per Year (KES)"
              type="number"
              value={planForm.price_per_year}
              onChange={(e) => setPlanForm({ ...planForm, price_per_year: parseInt(e.target.value) })}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <TextField
              fullWidth
              label="Max Students"
              type="number"
              value={planForm.max_students}
              onChange={(e) => setPlanForm({ ...planForm, max_students: parseInt(e.target.value) })}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <TextField
              fullWidth
              label="Max Cameras"
              type="number"
              value={planForm.max_cameras}
              onChange={(e) => setPlanForm({ ...planForm, max_cameras: parseInt(e.target.value) })}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={planForm.is_active}
                  onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(false)}>Cancel</Button>
          <Button onClick={handlePlanSubmit} variant="contained">
            {editingPlan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Token Dialog */}
      <Dialog open={tokenDialog} onClose={() => setTokenDialog(false)}>
        <DialogTitle>Generate Activation Token</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select
                value={tokenForm.plan_id}
                onChange={(e) => setTokenForm({ ...tokenForm, plan_id: e.target.value })}
              >
                {plans.filter(p => p.is_active).map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name} - KES {plan.price_per_term.toLocaleString()}/term
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subscription Type</InputLabel>
              <Select
                value={tokenForm.subscription_type}
                onChange={(e) => setTokenForm({ ...tokenForm, subscription_type: e.target.value as 'term' | 'year' })}
              >
                <MenuItem value="term">Term</MenuItem>
                <MenuItem value="year">Year</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="date"
              label="Expires At (Optional)"
              value={tokenForm.expires_at}
              onChange={(e) => setTokenForm({ ...tokenForm, expires_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenDialog(false)}>Cancel</Button>
          <Button onClick={handleTokenSubmit} variant="contained">
            Generate Token
          </Button>
        </DialogActions>
      </Dialog>

      {/* Term Dialog */}
      <Dialog open={termDialog} onClose={() => setTermDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTerm ? 'Edit Term' : 'Add Term'}</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2,
            mt: 1
          }}>
            <TextField
              fullWidth
              label="Term Name"
              value={termForm.name}
              onChange={(e) => setTermForm({ ...termForm, name: e.target.value })}
              sx={{ flex: '1 1 100%' }}
            />
            <TextField
              fullWidth
              label="Year"
              type="number"
              value={termForm.year}
              onChange={(e) => setTermForm({ ...termForm, year: parseInt(e.target.value) })}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <TextField
              fullWidth
              label="Term Number"
              type="number"
              value={termForm.term_number}
              onChange={(e) => setTermForm({ ...termForm, term_number: parseInt(e.target.value) })}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={termForm.start_date}
              onChange={(e) => setTermForm({ ...termForm, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={termForm.end_date}
              onChange={(e) => setTermForm({ ...termForm, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 200px', minWidth: '200px' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={termForm.is_active}
                  onChange={(e) => setTermForm({ ...termForm, is_active: e.target.checked })}
                />
              }
              label="Active Term"
              sx={{ flex: '1 1 100%' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermDialog(false)}>Cancel</Button>
          <Button onClick={handleTermSubmit} variant="contained">
            {editingTerm ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionManagement; 